// Health coverage, unemployment, age, family and retraining content (Parts added Sep 2026).
import type { BaselineRule } from './data'

export type Confidence = 'v' | 's' | 'x'
export const CONFIDENCE_LABEL: Record<Confidence, string> = {
  v: 'Verified',
  s: '1 source',
  x: 'Conflict',
}

/** State continuation ("mini-COBRA"): [level, has law, who it covers, max duration, notes, confidence]. */
export type Continuation = [number, string, string, string, string, Confidence]

export const CONTINUATION: Record<string, Continuation> = {
  AL: [0, '-', 'None', '-', 'No general law (domestic-abuse victims only)', 'v'],
  AK: [0, '-', 'None', '-', 'No general continuation law', 'v'],
  AZ: [
    3,
    'Yes',
    'Small employers (1-19)',
    '18 months (36 for some events)',
    'A.R.S. §20-2330, for plans since 2019',
    'v',
  ],
  AR: [1, 'Yes', 'Insured groups', '120 days', '', 'v'],
  CA: [
    3,
    'Yes',
    '2-19 employees (Cal-COBRA)',
    '36 months',
    'Also extends federal COBRA to 36 months total, for any employer size. Premium up to 110%.',
    'v',
  ],
  CO: [3, 'Yes', 'Insured groups', '18 months', '30-day election period', 'v'],
  CT: [
    3,
    'Yes',
    'Insured groups',
    '30 months (36 for some events)',
    'Separate law: 120 days of employer-paid coverage on closing or relocation',
    'v',
  ],
  DE: [2, 'Yes', '1-19 employees', '9 months', '', 'v'],
  FL: [3, 'Yes', '<20 employees', '18 months (29 if disabled)', 'Premium up to 115%', 'v'],
  GA: [
    1,
    'Yes',
    'Insured groups',
    '3 months',
    'Age 60+: extend federal COBRA until Medicare (O.C.G.A. §33-24-21.2), incl. spouse 60+ on divorce or death; premium up to 120%',
    'v',
  ],
  HI: [
    1,
    'Limited',
    '-',
    'Up to 3 months, sickness only',
    'HRS §393-15 continues employer premium contributions only while sick or hospitalized. There’s no layoff continuation law.',
    'v',
  ],
  ID: [0, '-', 'None', '-', 'No general continuation law (conversion rights only)', 'v'],
  IL: [
    2,
    'Yes',
    'Insured groups',
    '12 months',
    'Spouse 55+ on death, divorce or retirement: until Medicare; under 55: 2 years',
    'v',
  ],
  IN: [
    2,
    'Yes',
    'Small employers',
    '12 months',
    'Requires 1 year of employment and 90 days on the plan (Ind. Code §27-8-15-31.1)',
    'v',
  ],
  IA: [2, 'Yes', 'Insured groups', '9 months', '10-day election period', 'v'],
  KS: [3, 'Yes', 'Insured groups', '18 months', '', 'v'],
  KY: [3, 'Yes', 'Insured groups', '18 months', 'KRS 304.18-110', 'v'],
  LA: [
    2,
    'Yes',
    'Insured groups',
    '12 months',
    'Special rules for surviving and divorced spouses',
    'v',
  ],
  ME: [2, 'Yes', 'Insured groups', '12 months', '', 'v'],
  MD: [3, 'Yes', 'Insured groups', '18 months', '', 'v'],
  MA: [
    3,
    'Yes',
    '2-19 employees (insured)',
    '18 months; 36 for death, divorce, Medicare',
    'Mirrors federal durations. Separate closing law also requires continued coverage',
    'v',
  ],
  MI: [
    0,
    '-',
    'None',
    '-',
    'No general continuation law. Only conversion rights with nonprofit carriers',
    'v',
  ],
  MN: [
    3,
    'Yes',
    'All insured employers',
    '18 months (longer if totally disabled)',
    '60-day election period',
    'v',
  ],
  MS: [2, 'Yes', 'Insured groups', '12 months', '', 'v'],
  MO: [
    3,
    'Yes',
    'Employers not under federal COBRA',
    '18 months (36 for death, divorce)',
    'Mirrors federal COBRA (RSMo §376.428)',
    'v',
  ],
  MT: [
    1,
    'Limited',
    '-',
    'Up to 1 year, narrow triggers',
    'Sources disagree: one lists 6 months’ general continuation, another says only reduced-hours cases are covered',
    'x',
  ],
  NE: [1, 'Yes', 'Involuntary terminations', '6 months', '10-day election period', 'v'],
  NV: [
    0,
    '-',
    'None',
    '-',
    'General law repealed in 2013 (disability-leave continuation only)',
    'v',
  ],
  NH: [3, 'Yes', 'Insured groups', '18 months (36 for some events)', '', 'v'],
  NJ: [3, 'Yes', 'Small employers (sources differ: <20 or 2-50)', '18 months', '', 'v'],
  NM: [1, 'Yes', 'Insured groups', '6 months', '', 'v'],
  NY: [
    3,
    'Yes',
    'All sizes (insured plans)',
    '36 months',
    'Also extends federal COBRA to 36 months total for insured plans (NY DFS)',
    'v',
  ],
  NC: [3, 'Yes', 'Insured groups', '18 months', '', 'v'],
  ND: [2, 'Yes', 'Insured groups', '39 weeks', 'Former spouse: up to 36 months', 'v'],
  OH: [2, 'Yes', 'Involuntary terminations', '12 months', '', 'v'],
  OK: [1, 'Yes', 'Groups not under federal COBRA', '63 days', '', 'v'],
  OR: [2, 'Yes', '<20 employees', '9 months', 'Only a 10-day election period', 'v'],
  PA: [2, 'Yes', '2-19 employees (insured)', '9 months', 'Premium up to 105%', 'v'],
  RI: [3, 'Yes', 'Insured groups', '18 months', '', 'v'],
  SC: [1, 'Yes', 'Insured groups', '6 months', '', 'v'],
  SD: [3, 'Yes', 'All group plans', '18 months (29 if disabled)', 'SDCL §58-18-7.5', 'v'],
  TN: [1, 'Yes', 'Insured groups', '3 months (15 for death, divorce)', '', 'v'],
  TX: [2, 'Yes', 'All sizes (insured)', '9 months; or 6 more after federal COBRA', '', 'v'],
  UT: [2, 'Yes', 'Groups not under federal COBRA', '12 months', '', 'v'],
  VT: [
    3,
    'Yes',
    'All group plans, incl. self-insured',
    '18 months',
    '8 V.S.A. §4090a covers self-insured plans too, which is unusual',
    'v',
  ],
  VA: [2, 'Yes', 'Groups not under federal COBRA', '12 months', '', 'v'],
  WA: [0, '-', 'None', '-', 'No general continuation law (labor-dispute continuation only)', 'v'],
  WV: [3, 'Yes', 'Involuntary layoffs', '18 months', '', 'v'],
  WI: [3, 'Yes', 'Insured groups', '18 months', '', 'v'],
  WY: [2, 'Yes', '2-19 employees', '12 months', 'W.S. §26-19-113; premium up to 102%', 'v'],
}

/** How severance affects unemployment benefits: [level, rule, confidence]. */
export type UnemploymentRule = [number, string, Confidence]

export const UNEMPLOYMENT_LEVELS: Record<number, string> = {
  3: 'Delays benefits',
  2: 'Reduces weekly benefit',
  1: 'Depends on how it’s paid',
  0: 'No effect',
}

export const UNEMPLOYMENT: Record<string, UnemploymentRule> = {
  AL: [1, 'Depends on whether payments show a continuing employment relationship', 's'],
  AK: [2, 'Deducted from the weekly benefit', 's'],
  AZ: [1, 'Counts only if allocated to a week being claimed', 's'],
  AR: [3, 'Disqualified for the number of weeks of wages the payment represents', 's'],
  CA: [0, 'True severance isn’t wages for UI', 'v'],
  CO: [3, 'Delayed: total severance divided by usual weekly wage = weeks postponed', 's'],
  CT: [3, 'Disqualified for the period the payment covers', 's'],
  DE: [1, 'Decided case by case', 's'],
  FL: [3, 'Disqualified for weeks in which severance is received', 'v'],
  GA: [
    3,
    'Lump sums are spread over weeks by weekly wage. A week is disqualified only if its share exceeds the weekly benefit',
    'v',
  ],
  HI: [1, 'A lump sum counts only in the week received; periodic payments affect each week', 's'],
  ID: [2, 'Deducted for the weeks it’s allocated to', 's'],
  IL: [0, 'IDES treats severance as pay for past service, not wages', 'v'],
  IN: [2, 'Subtracted from benefits for the weeks it’s allocated to', 'v'],
  IA: [2, 'Deducted dollar-for-dollar for the weeks it applies to', 's'],
  KS: [2, 'Reduces the weekly benefit', 's'],
  KY: [0, 'Not deductible', 's'],
  LA: [2, 'Deducted for the weeks it covers', 'v'],
  ME: [3, 'A week in which severance is received is disqualified', 's'],
  MD: [3, 'Allocated to the weeks after separation', 's'],
  MA: [
    1,
    'Disqualifying for the weeks covered, unless paid in exchange for a signed release of claims',
    's',
  ],
  MI: [2, 'Reduces the benefit in weeks the employer allocates it to', 's'],
  MN: [3, 'Delayed by the number of weeks the severance represents', 'v'],
  MS: [1, 'Full-wage severance counts as continued employment; smaller amounts are deducted', 's'],
  MO: [0, 'Not counted against benefits', 's'],
  MT: [1, 'May reduce only the week of separation', 'v'],
  NE: [3, 'A lump sum is prorated across weeks', 's'],
  NV: [3, 'A week in which severance is received is disqualified', 's'],
  NH: [3, 'Allocated forward from separation', 's'],
  NJ: [0, 'Service-based severance isn’t counted', 'v'],
  NM: [0, 'Severance and buyouts aren’t wages', 's'],
  NY: [3, 'Ineligible only while weekly severance exceeds the maximum weekly benefit', 'v'],
  NC: [3, 'Treated like earnings from the last employer', 's'],
  ND: [
    1,
    'Counts only if not based on years of service and meant to cover time after separation',
    's',
  ],
  OH: [2, 'Deductible, reducing weekly benefits', 'v'],
  OK: [1, 'Deducted if required by law, contract or policy; purely voluntary severance isn’t', 's'],
  OR: [0, 'Not reportable', 's'],
  PA: [1, 'Only the portion above 40% of the state average annual wage is deducted', 's'],
  RI: [3, 'Allocated weekly from the last day of work, up to 26 weeks', 's'],
  SC: [0, 'Severance isn’t counted against benefits', 's'],
  SD: [2, 'Deducted dollar-for-dollar', 's'],
  TN: [3, 'Disqualifies any week with severance or pay in lieu of notice', 's'],
  TX: [3, 'Allocated to the weeks it covers', 'v'],
  UT: [3, 'Lump sums assigned to the weeks after the last day', 's'],
  VT: [3, 'Allocated to the weeks after separation', 's'],
  VA: [2, 'Deducted from benefits once the employer reports it', 'v'],
  WA: [1, 'Usually no effect when not assigned to any period', 's'],
  WV: [0, 'Service-based severance isn’t deductible', 's'],
  WI: [1, 'Reduces a week only if definitely allocated to it', 's'],
  WY: [2, 'A week is disqualified if its severance equals or exceeds the weekly benefit', 's'],
}

export type TimelineStep = { when: string; what: string; why: string; warn?: boolean }

export const TIMELINE: TimelineStep[] = [
  {
    when: 'Last day of work',
    what: 'Group coverage usually ends on the last day or at the end of that month',
    why: 'The plan document decides which. Final pay, PTO payout and any separation notice follow the state rules in the explorer.',
  },
  {
    when: 'Within 30 days',
    what: 'Employer notifies the COBRA administrator',
    why: 'The administrator then has 14 days to send the election notice. When the employer is also the administrator, it has 44 days in total.',
  },
  {
    when: 'Within 30 days of losing coverage',
    what: 'Deadline to join a spouse’s employer plan',
    why: 'HIPAA special enrollment. It’s 60 days if the coverage lost was Medicaid or CHIP.',
  },
  {
    when: '60 days before to 60 days after',
    what: 'ACA marketplace special enrollment period',
    why: 'Losing job-based coverage opens a 60-day window. Enhanced premium subsidies expired at the end of 2025, so marketplace premiums are higher in 2026 than in 2025, though the original subsidies remain.',
  },
  {
    when: '60 days from the election notice',
    what: 'COBRA election deadline',
    why: 'Coverage is retroactive to the day after the old coverage ended. That means someone can wait, and elect only if they need care during the window.',
  },
  {
    when: '45 days after electing',
    what: 'First COBRA premium due',
    why: 'After that, monthly premiums have a 30-day grace period.',
  },
  {
    when: '8 months after the job ends · age 65+',
    what: 'Medicare Part B special enrollment period closes',
    why: 'The 8 months run from the end of employment, not the end of COBRA. COBRA isn’t coverage based on current employment, so it doesn’t protect against the Part B late penalty of 10% per year, which lasts for life.',
    warn: true,
  },
  {
    when: '18 months',
    what: 'COBRA ends for the employee',
    why: 'It can be extended to 29 months for disability, and spouses and dependents can reach 36 months after a second qualifying event. State extensions (CA, NY, GA age 60+, IL spouse 55+) can add more.',
  },
]

export const COBRA_RULES: BaselineRule[] = [
  {
    h: 'Who is covered',
    law: '29 U.S.C. §1161 · 20+ employees',
    pts: [
      'Private employers with 20+ employees (state and local governments via the PHS Act).',
      'Qualified beneficiaries: the employee, a covered spouse and covered dependent children. Each can elect COBRA independently.',
      'Termination for any reason except "gross misconduct" (quitting and layoffs included) triggers COBRA.',
    ],
  },
  {
    h: 'How long it lasts',
    law: 'Qualifying events',
    pts: [
      '18 months: termination or reduced hours.',
      '29 months: if SSA finds a beneficiary disabled within the first 60 days. The premium can rise to 150% for months 19-29.',
      '36 months for spouse and dependents: the employee’s death, divorce or legal separation, the employee becoming entitled to Medicare, or a child aging out, including as a second event during the first 18 months.',
    ],
  },
  {
    h: 'What it costs',
    law: 'Premium rules',
    pts: [
      'Up to 102% of the full plan cost, employer and employee shares combined. This is the "COBRA shock" after a layoff.',
      'There’s no federal COBRA subsidy in 2026. The 2021 ARPA subsidy ended in September 2021.',
    ],
  },
  {
    h: 'How severance interacts with it',
    law: 'Severance design',
    pts: [
      'Employers often pay or reimburse several months of COBRA as part of severance.',
      'Premiums the employer pays directly to the plan are generally not taxable to the employee. Cash given "for COBRA" is taxable wages.',
      'Subsidized COBRA can delay the move to marketplace coverage but doesn’t close the marketplace window, which runs from the loss of the original coverage.',
    ],
  },
  {
    h: 'When it ends early',
    law: 'Termination events',
    pts: [
      'Unpaid premium after the 30-day grace period.',
      'The beneficiary gets another group plan after electing COBRA.',
      'The beneficiary becomes entitled to Medicare after electing COBRA (the spouse’s COBRA continues).',
      'The employer stops offering any group health plan.',
    ],
  },
  {
    h: 'Notice penalties',
    law: 'ERISA §502(c)(1)',
    pts: [
      'A late or missing election notice can cost the plan administrator up to $110 a day, plus the claims that should have been covered.',
      'States with mini-COBRA laws have their own notice duties. Some are strict: Oregon’s election window is only 10 days.',
    ],
  },
]

export const MEDICARE_RULES: BaselineRule[] = [
  {
    h: 'Already 65+ when the job ends',
    law: 'Medicare Part B special enrollment',
    pts: [
      'Anyone who delayed Part B because of job-based coverage gets an 8-month window from the end of employment or coverage, whichever comes first.',
      'COBRA doesn’t extend this window. Waiting for COBRA to run out leads to a gap and a permanent penalty of 10% per 12 months late.',
      'Once on Medicare, Medicare pays first and COBRA pays second. The COBRA plan can reduce its payments as if Part B were in place, even if it isn’t.',
    ],
  },
  {
    h: 'On Medicare before electing COBRA',
    law: 'COBRA + Medicare order',
    pts: [
      'Someone already entitled to Medicare when COBRA starts can keep both.',
      'Becoming entitled to Medicare after electing COBRA ends that person’s COBRA.',
      'In either case, a spouse under 65 keeps COBRA and may get 36 months.',
    ],
  },
  {
    h: 'Medigap and drug coverage timing',
    law: 'Medicare supplements',
    pts: [
      'The 6-month Medigap open enrollment starts when Part B begins at 65+. During it, insurers can’t price by health history.',
      'Part D: a gap over 63 days without creditable drug coverage brings a lifetime late penalty. Most COBRA plans count as creditable, but the plan’s notice confirms it.',
    ],
  },
  {
    h: 'Under 65: individual insurance',
    law: 'ACA marketplace',
    pts: [
      'Losing job coverage gives a 60-day special enrollment period, on HealthCare.gov or the state’s own exchange.',
      'Income for subsidies is projected annual household income. A layoff mid-year can lower it, but a large lump-sum severance counts in the year it’s paid.',
      'Enhanced subsidies ended 31 Dec 2025, so 2026 subsidies use the original ACA formula, including the 400% federal poverty level cap.',
    ],
    but: 'Choosing COBRA voluntarily doesn’t block the marketplace later, but dropping COBRA mid-year outside open enrollment doesn’t open a new window. Running out of COBRA does.',
  },
  {
    h: 'Medicaid',
    law: 'Income-based',
    pts: [
      'Eligibility is based on monthly income, so someone with no income after a layoff may qualify even with savings, in the 40+ states that expanded Medicaid.',
      'Severance paid out over time counts as income in the months it’s paid.',
    ],
  },
  {
    h: 'Taxes on Medicare itself',
    law: 'FICA',
    pts: [
      'Severance is subject to the 1.45% Medicare tax (plus 0.9% above $200K) like any wages. That’s the only place "Medicare" touches the severance payment itself.',
    ],
  },
]

export type AgeRung = { age: string; h: string; pts: string[] }

export const AGE_LADDER: AgeRung[] = [
  {
    age: '26',
    h: 'Dependent children',
    pts: [
      'Children can stay on a parent’s plan until 26. Aging off is a COBRA qualifying event, with 36 months for the child.',
      'New York lets young adults buy into a parent’s insured plan until 29 (NY DFS).',
    ],
  },
  {
    age: '40',
    h: 'Age-discrimination protections begin',
    pts: [
      'ADEA protects workers 40+ at employers with 20+ employees. Many states cover smaller employers or younger workers.',
      'Releases of age claims must meet OWBPA: 21 days to consider (45 in a group layoff), 7 days to revoke, advice to consult a lawyer, and group disclosures of the ages and job titles of those selected and not selected. The only release rule that depends on age.',
      'In a layoff, the selection method can be challenged if it falls disproportionately on workers 40+.',
    ],
  },
  {
    age: '55',
    h: 'Rule of 55 · Illinois spouses',
    pts: [
      'Someone who leaves the job in or after the year they turn 55 can withdraw from that employer’s 401(k) or 403(b) without the 10% early-withdrawal penalty. IRAs don’t qualify, so a rollover gives this up.',
      'Illinois: a spouse 55+ who loses coverage through the employee’s death, divorce or retirement can continue it until Medicare.',
    ],
  },
  {
    age: '60',
    h: 'Georgia continuation',
    pts: [
      'Georgia lets employees 60+ (and spouses 60+ on divorce or death) extend federal COBRA until Medicare eligibility, for insured plans, at up to 120% of the premium.',
    ],
  },
  {
    age: '62',
    h: 'Social Security',
    pts: [
      'Earliest age to claim retirement benefits.',
      'Severance counts as a "special payment" for work done earlier. It doesn’t count toward the retirement earnings test, so it doesn’t reduce benefits for someone who has started claiming.',
    ],
  },
  {
    age: '65',
    h: 'Medicare',
    pts: [
      'The Part B 8-month window from job loss applies.',
      'If the employee became entitled to Medicare within 18 months before the layoff, the spouse and dependents get COBRA for up to 36 months from the date of entitlement.',
      'ADEA allows a closing or layoff severance to be reduced by the value of retiree health coverage or extra pension benefits the employee becomes eligible for (29 U.S.C. §623(l)(2)). It’s the one age-linked severance offset the law permits.',
    ],
  },
]

export const AGE_NO_CHANGE =
  'Final-pay deadlines, PTO payout, WARN notice, the NJ, ME and HI severance formulas (they depend on years of service, not age), separation notices, and unemployment eligibility. Severance can’t be reduced because of age, and choosing who gets laid off in a way that falls hardest on workers 40+ can be challenged as age discrimination under the ADEA (disparate impact).'

export const FAMILY_RULES: BaselineRule[] = [
  {
    h: 'Spouse and children on COBRA',
    law: 'Independent election rights',
    pts: [
      'A spouse and children covered the day before the job ended are qualified beneficiaries in their own right. They can elect COBRA even if the employee doesn’t.',
      'Termination gives 18 months. A second event during that time (divorce, the employee’s death or Medicare entitlement) extends spouse and dependent coverage to 36 months.',
      'One election notice to the household is allowed only if it goes to an address where the spouse lives.',
    ],
  },
  {
    h: 'Joining a spouse’s employer plan',
    law: 'HIPAA special enrollment',
    pts: [
      'Losing coverage lets the employee and family join a spouse’s employer plan mid-year if they ask within 30 days.',
      'That’s often cheaper than COBRA, since the spouse’s employer pays part of the premium.',
    ],
  },
  {
    h: 'Marketplace and the "family glitch" fix',
    law: 'ACA · IRS 2022 rule',
    pts: [
      'Since 2023, whether a spouse’s employer plan is "affordable" for the family is judged on the family premium. Family members can get marketplace subsidies even when the spouse’s employee-only coverage is affordable.',
      'The 60-day loss-of-coverage window covers the whole household.',
    ],
  },
  {
    h: 'When the laid-off employee is on Medicare',
    law: 'Medicare + COBRA',
    pts: [
      'A spouse under 65 has no Medicare of their own, so the spouse needs COBRA, the marketplace or their own job plan.',
      'If the employee had become entitled to Medicare within the 18 months before the layoff, the spouse’s COBRA can run 36 months from the date of entitlement.',
    ],
  },
  {
    h: 'When the spouse is still working',
    law: 'Medicare Part B',
    pts: [
      'A 65+ employee who loses their job can delay Part B without penalty if covered by a spouse’s employer plan based on the spouse’s current employment (generally at employers with 20+ employees).',
      'When that spouse retires, the 8-month Part B window starts.',
    ],
  },
  {
    h: 'State spouse rules',
    law: 'State continuation',
    pts: [
      'Illinois: spouse 55+ on the employee’s death, divorce or retirement can continue coverage until Medicare.',
      'Georgia: spouse 60+ on divorce or death can extend COBRA until Medicare.',
      'California and New York: extend to 36 months total.',
    ],
    but: 'Severance, final pay, WARN notice and unemployment are never affected by the spouse’s job, income or benefits.',
  },
]

export const TRAINING_RULES: BaselineRule[] = [
  {
    h: 'WIOA Dislocated Worker Program',
    law: 'Workforce Innovation and Opportunity Act',
    pts: [
      'The main federal program since TAA ended. Offers career services, individual training accounts to pay for approved courses, and supportive services.',
      'Open to laid-off workers unlikely to return to their occupation, and to people affected by closings. Apply at an American Job Center.',
      'It doesn’t provide income support. Unemployment insurance does that.',
    ],
  },
  {
    h: 'Rapid Response',
    law: 'WIOA · triggered by WARN',
    pts: [
      'Every state has a dislocated-worker unit that receives WARN notices and sends a team on site before the layoff, to explain UI, WIOA training and health-coverage options.',
      'Employers without a WARN duty can request it voluntarily.',
    ],
  },
  {
    h: 'Unemployment while in training',
    law: 'FUTA §3304(a)(8)',
    pts: [
      'Federal law bars every state from denying UI because a claimant is in approved training. The work-search requirement is generally waived.',
      'This extends eligibility during regular benefits but doesn’t add weeks. For extra weeks, see WA, OR and ME below.',
    ],
  },
  {
    h: 'Trade Adjustment Assistance',
    law: 'Ended 2022',
    pts: [
      'TAA stopped accepting new petitions on 1 July 2022, and Congress hasn’t reauthorized it as of 2026. Only workers certified before then still receive benefits.',
      'Older guides that point laid-off workers to TAA are out of date.',
    ],
  },
  {
    h: 'National Dislocated Worker Grants',
    law: 'US DOL',
    pts: [
      'Short-term federal grants to states after large layoffs or disasters, for retraining and temporary jobs. States apply for them; individuals don’t.',
    ],
  },
  {
    h: 'Severance and training money',
    law: 'Coordination',
    pts: [
      'Severance generally doesn’t bar WIOA eligibility.',
      'Whether severance delays UI depends on the state. See the unemployment section. Check with the state UI agency before planning training around UI.',
    ],
  },
]

export const TRAINING_STATES: { h: string; list: [string, string][] }[] = [
  {
    h: 'Extra unemployment weeks while retraining',
    list: [
      [
        'WA',
        'Training Benefits Program: up to 26 extra weeks of UI for dislocated workers in approved training. Commissioner-Approved Training waives job search.',
      ],
      [
        'OR',
        'Training UI: 1-26 weeks of supplemental benefits to finish approved career or technical training.',
      ],
      ['ME', '26 M.R.S. §1196: extended benefits for dislocated workers in approved training.'],
      [
        'CA',
        'California Training Benefits: collect UI while in approved training without job-search requirements.',
      ],
    ],
  },
  {
    h: 'State-funded retraining funds',
    list: [
      [
        'CA',
        'Employment Training Panel: funded by an employer payroll tax; pays for training for incumbent and unemployed workers.',
      ],
      [
        'NJ',
        'Workforce Development Partnership: training grants for displaced workers, funded by payroll contributions.',
      ],
      [
        'MN',
        'Dislocated Worker Program: state-funded through an employer assessment on top of WIOA.',
      ],
      ['TX', 'Skills Development Fund: up to $500K grants for employer-college custom training.'],
      [
        'MA',
        'Workforce Training Fund: up to $125K employer grants to train current and new hires.',
      ],
    ],
  },
]
